import type { ReactNode } from 'react';

import { cn } from '../cn';
import { ErrorIcon, Icon, InfoIcon, SuccessIcon, WarningIcon, type IconName, type IconTone } from '../icons';

type Tone = 'info' | 'success' | 'warning' | 'negative' | 'neutral';

/**
 * Every tone sits on the white surface with a hairline border, a 3px tone rule at the inline start and a tone icon.
 * No tinted (cream / pink / blue) boxes: the rule and icon carry the tone, the words carry the meaning.
 */
const tones: Record<Tone, { edge: string; icon: ReactNode; iconTone: IconTone }> = {
  info: { edge: 'edge-info', icon: <InfoIcon className="text-info" />, iconTone: 'info' },
  success: { edge: 'edge-positive', icon: <SuccessIcon className="text-positive" />, iconTone: 'positive' },
  warning: { edge: 'edge-warning', icon: <WarningIcon className="text-warning-edge" />, iconTone: 'warning' },
  negative: { edge: 'edge-negative', icon: <ErrorIcon className="text-negative" />, iconTone: 'negative' },
  neutral: { edge: 'edge-neutral', icon: <InfoIcon className="text-ink-muted" />, iconTone: 'muted' },
};

export interface AlertProps {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  /** Concept glyph instead of the tone's default icon (e.g. concept.hold), drawn in the tone colour. */
  icon?: IconName;
  className?: string;
  role?: 'alert' | 'status';
}

/**
 * Inline operational message: states the fact, the consequence and the owner. No exclamation marks, no stacked icons.
 * `success` confirms a completed state that stays on screen (e.g. an import committed); transient confirmations
 * of the user's own action are toasts.
 */
export function Alert({ tone = 'info', title, children, action, icon, className, role }: AlertProps) {
  const t = tones[tone];
  return (
    <div
      role={role ?? (tone === 'negative' ? 'alert' : 'status')}
      data-tone={tone}
      className={cn('edge-start flex items-start gap-2.5 rounded-sm border border-border bg-surface py-2.5 ps-3.5 pe-3 text-ink', t.edge, className)}
    >
      <span className="mt-0.5">{icon ? <Icon name={icon} tone={t.iconTone} /> : t.icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-body">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="text-ink-secondary">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
